/**
 * MetalVision Kiosko - Device Authentication
 * Autenticación basada en identificación única del dispositivo
 * 
 * Estrategia de seguridad:
 * 1. Generar fingerprint único del dispositivo (TV)
 * 2. Registrar el dispositivo en el backend (una sola vez)
 * 3. Validar el fingerprint en cada inicio de sesión
 * 4. Bloquear acceso desde dispositivos no autorizados
 */

// ========================================
// Device Identity
// ========================================

const FINGERPRINT_KEY = 'kiosko_device_fingerprint';
const DEVICE_CODE_KEY = 'kiosko_device_code';

/**
 * Identificador del dispositivo: aleatorio, se genera una vez y se guarda en localStorage.
 *
 * Antes se derivaba de las características del navegador (user agent, pantalla, canvas,
 * WebGL…), pero eso no es estable: Brave aleatoriza canvas/WebGL en cada sesión, una
 * actualización cambia el user agent y otra pantalla cambia la resolución. Cada cambio
 * daba un dispositivo nuevo sin aprobar. Es el mismo esquema que las apps de obra y taller.
 *
 * Los dispositivos ya aprobados conservan la huella antigua: es la que quedó guardada
 * en `kiosko_device_fingerprint` tras su última autenticación, y se reutiliza tal cual.
 */
async function generateDeviceFingerprint() {
  let fingerprint = localStorage.getItem(FINGERPRINT_KEY);
  if (!fingerprint) {
    // crypto.getRandomValues funciona también fuera de HTTPS (crypto.randomUUID no).
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    const id = Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
    fingerprint = await hashString(`metalvision-kiosko|${id}`);
    localStorage.setItem(FINGERPRINT_KEY, fingerprint);
  }
  // Pide al navegador que no borre este almacenamiento por falta de espacio o inactividad.
  navigator.storage?.persist?.().catch(() => {});
  return fingerprint;
}

/**
 * Código legible del dispositivo (KIOSK-XXXXXXXX) para que la oficina lo encuentre.
 */
function getDeviceCode() {
  return localStorage.getItem(DEVICE_CODE_KEY);
}

function setDeviceCode(code) {
  if (code) localStorage.setItem(DEVICE_CODE_KEY, code);
}

/**
 * Genera un hash SHA-256 de un string
 * Fallback para navegadores sin crypto.subtle (Safari HTTP)
 */
async function hashString(str) {
  // Verificar si crypto.subtle está disponible
  if (crypto.subtle && crypto.subtle.digest) {
    try {
      const encoder = new TextEncoder();
      const data = encoder.encode(str);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    } catch (error) {
      console.warn('crypto.subtle.digest failed, using fallback hash');
    }
  }
  
  // Fallback: Simple hash function (no criptográfico, pero suficiente para fingerprinting)
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  // Convertir a hex de 64 caracteres (simular SHA-256)
  const hexHash = Math.abs(hash).toString(16).padStart(8, '0');
  return hexHash.repeat(8); // 64 caracteres como SHA-256
}

// ========================================
// Device Registration & Authentication
// ========================================

/**
 * Registra el dispositivo en el backend (solo primera vez)
 */
async function registerDevice(apiUrl) {
  const fingerprint = await generateDeviceFingerprint();
  
  // Información adicional del dispositivo
  const deviceInfo = {
    fingerprint,
    userAgent: navigator.userAgent,
    screenResolution: `${screen.width}x${screen.height}`,
    platform: navigator.platform,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    registeredAt: new Date().toISOString()
  };
  
  try {
    const response = await fetch(`${apiUrl}/api/hr/devices/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(deviceInfo)
    });
    
    if (!response.ok) {
      throw new Error(`Registration failed: ${response.status}`);
    }
    
    const result = await response.json();
    
    setDeviceCode(result.device?.deviceId);

    return result;
  } catch (error) {
    console.error('Device registration failed:', error);
    throw error;
  }
}

/**
 * Autentica el dispositivo en cada inicio
 * La autenticación se basa en el fingerprint del dispositivo.
 * El backend valida que el dispositivo esté registrado y activo.
 */
async function authenticateDevice(apiUrl) {
  const fingerprint = await generateDeviceFingerprint();

  try {
    const response = await fetch(`${apiUrl}/api/hr/devices/authenticate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        fingerprint
      })
    });
    
    if (response.status === 404) {
      // Dispositivo no registrado
      throw new Error('DEVICE_NOT_REGISTERED');
    }
    
    if (response.status === 403) {
      const errorData = await response.json().catch(() => ({}));
      // hr-service responde 'Device pending approval' o 'Device is blocked'.
      if (/pending/i.test(String(errorData.error ?? ''))) {
        throw new Error('DEVICE_PENDING_APPROVAL');
      }
      throw new Error('DEVICE_BLOCKED');
    }
    
    if (!response.ok) {
      throw new Error(`Authentication failed: ${response.status}`);
    }
    
    const result = await response.json();
    
    setDeviceCode(result.device?.deviceId);
    localStorage.setItem('kiosko_session_token', result.sessionToken);
    localStorage.setItem('kiosko_session_expires', result.expiresAt);
    localStorage.setItem('kiosko_display_type', result.device?.displayType || 'kiosk');
    storePanelLayout(result.device?.displayConfig);

    return result;
  } catch (error) {
    // Mejorar mensaje de error para desarrollo
    if (error.message.includes('Failed to fetch')) {
      const devError = new Error('NETWORK_ERROR');
      devError.originalError = error;
      devError.apiUrl = apiUrl;
      throw devError;
    }
    console.error('Device authentication failed:', error);
    throw error;
  }
}

/**
 * Verifica si la sesión es válida
 */
function isSessionValid() {
  const sessionToken = localStorage.getItem('kiosko_session_token');
  const expiresAt = localStorage.getItem('kiosko_session_expires');
  
  if (!sessionToken || !expiresAt) {
    return false;
  }
  
  const now = new Date();
  const expiry = new Date(expiresAt);
  
  return now < expiry;
}

/**
 * Obtiene el token de sesión actual
 */
function getSessionToken() {
  if (!isSessionValid()) {
    return null;
  }
  return localStorage.getItem('kiosko_session_token');
}

/**
 * Cierra la sesión
 */
function logout() {
  localStorage.removeItem('kiosko_session_token');
  localStorage.removeItem('kiosko_session_expires');
}

/**
 * Renovar sesión automáticamente
 */
async function renewSession(apiUrl) {
  const sessionToken = getSessionToken();
  
  if (!sessionToken) {
    throw new Error('No active session');
  }
  
  try {
    const response = await fetch(`${apiUrl}/api/hr/devices/renew`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ sessionToken })
    });
    
    if (!response.ok) {
      throw new Error(`Session renewal failed: ${response.status}`);
    }
    
    const result = await response.json();
    
    localStorage.setItem('kiosko_session_token', result.sessionToken);
    localStorage.setItem('kiosko_session_expires', result.expiresAt);
    if (result.displayType) localStorage.setItem('kiosko_display_type', result.displayType);
    if (result.displayConfig) storePanelLayout(result.displayConfig);

    return result;
  } catch (error) {
    console.error('Session renewal failed:', error);
    throw error;
  }
}

/**
 * Tipo de pantalla del dispositivo según la última autenticación/renovación.
 * 'kiosk' → app de taller; 'panel' → tablero en tele (redirige al backoffice).
 */
function getDisplayType() {
  return localStorage.getItem('kiosko_display_type');
}

const PANEL_LAYOUT_KEY = 'kiosko_panel_layout';

/** Guarda display_config.layout (o lo borra si no viene: vale el de por defecto). */
function storePanelLayout(displayConfig) {
  const layout = displayConfig?.layout;
  if (layout) localStorage.setItem(PANEL_LAYOUT_KEY, layout);
  else localStorage.removeItem(PANEL_LAYOUT_KEY);
}

/** Pizarra que tiene que abrir la tele ('interactivo' | 'clasico'), o null si no hay. */
function getPanelLayout() {
  return localStorage.getItem(PANEL_LAYOUT_KEY);
}

// ========================================
// Exports
// ========================================

export {
  generateDeviceFingerprint,
  getDeviceCode,
  setDeviceCode,
  registerDevice,
  authenticateDevice,
  isSessionValid,
  getSessionToken,
  getDisplayType,
  getPanelLayout,
  logout,
  renewSession
};
